import React, { useState, useEffect } from 'react';

const complaintTypes = [
  { value: '', label: 'Sélectionnez un type' },
  { value: 'not_received', label: 'Colis non reçu' },
  { value: 'damaged', label: 'Colis endommagé' },
  { value: 'lost', label: 'Colis perdu' },
  { value: 'other', label: 'Autre' },
];

const countryOptions = [
  { code: '+229', label: 'Bénin', iso: 'BJ' },
  { code: '+228', label: 'Togo', iso: 'TG' },
  { code: '+225', label: "Côte d'Ivoire", iso: 'CI' },
  { code: '+221', label: 'Sénégal', iso: 'SN' },
  { code: '+226', label: 'Burkina Faso', iso: 'BF' },
  { code: '+224', label: 'Guinée', iso: 'GN' },
  { code: '+245', label: 'Guinée-Bissau', iso: 'GW' },
  { code: '+227', label: 'Niger', iso: 'NE' },
  { code: '+234', label: 'Nigéria', iso: 'NG' },
  { code: '+220', label: 'Gambie', iso: 'GM' },
  { code: '+233', label: 'Ghana', iso: 'GH' },
];

const ComplaintPage = () => {
  const [formData, setFormData] = useState({
    trackingId: '',
    name: '',
    phone: '',
    complaintType: '',
    description: '',
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState('+229');
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const urlParams = new URLSearchParams(window.location.search);
    const trackingId = urlParams.get('tracking-id');
    if (trackingId && trackingId.trim()) {
      setFormData(prev => ({ ...prev, trackingId }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneInput = (e) => {
    const raw = e.target.value.replace(/[^\d\s]/g, '');
    setFormData(prev => ({ ...prev, phone: raw }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleCountryChange = (e) => {
    setSelectedCountryCode(e.target.value);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = files.filter(f => f.size <= maxSize && f.type.startsWith('image/'));

    if (validFiles.length !== files.length) {
      setErrors(prev => ({ ...prev, attachments: 'Seules les images de moins de 5 Mo sont acceptées.' }));
    } else if (errors.attachments) {
      setErrors(prev => ({ ...prev, attachments: '' }));
    }

    setAttachments(prev => [...prev, ...validFiles].slice(0, 5));
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.trackingId.trim()) newErrors.trackingId = 'Le code de suivi est requis.';
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis.';
    if (!formData.phone.trim() || formData.phone.replace(/\s/g, '').length < 8) newErrors.phone = 'Numéro de téléphone invalide.';
    if (!formData.complaintType) newErrors.complaintType = 'Veuillez sélectionner un type de réclamation.';
    if (!formData.description.trim()) newErrors.description = 'Veuillez décrire votre problème.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const form = new FormData();
      form.append('tracking_id', formData.trackingId.trim());
      form.append('customer_name', formData.name.trim());
      form.append('customer_phone', selectedCountryCode + formData.phone.replace(/\s+/g, ''));
      form.append('complaint_type', formData.complaintType);
      form.append('description', formData.description.trim());
      attachments.forEach(file => form.append('attachments', file));

      const apiBase = process.env.REACT_APP_API_ENDPOINT;
      const res = await fetch(`${apiBase}/v1/public/complaint`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }

      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error submitting complaint:', err);
      setSubmitError('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="mx-auto bg-gray-50 min-h-screen">
        <div className="max-w-2xl mx-4 sm:mx-auto mt-20 mb-10 pt-16">
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e6f7ee' }}>
              <svg className="w-8 h-8" style={{ color: '#009B36' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Réclamation envoyée</h2>
            <p className="text-gray-600 mb-6">
              Votre réclamation pour le colis <span className="font-semibold">{formData.trackingId}</span> a bien été enregistrée. Notre équipe vous contactera dans les plus brefs délais.
            </p>
            <a
              href={`/tracking?tracking-id=${formData.trackingId}&submit=1`}
              className="inline-block bg-primary-custom hover:bg-primary-custom/90 text-white font-bold px-6 py-3 rounded transition"
            >
              Retour au suivi
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto bg-gray-50 min-h-screen">
      <div className="max-w-2xl mx-4 sm:mx-auto mt-20 mb-10 pt-4">
        <h1 className="text-4xl sm:text-5xl font-bold text-left mb-2">
          <span className="text-black">Faire une </span>
          <span className="text-primary-custom">réclamation</span>
        </h1>
        <p className="text-gray-500 text-lg font-medium mb-8 mt-4">
          Remplissez le formulaire ci-dessous pour signaler un problème avec votre livraison.
        </p>

        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 space-y-5">
          {/* Tracking ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code de suivi</label>
            <input
              type="text"
              name="trackingId"
              value={formData.trackingId}
              onChange={handleChange}
              placeholder="Ex: OGB1046610097"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition ${
                errors.trackingId ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
              }`}
            />
            {errors.trackingId && <p className="mt-1 text-sm text-red-600">{errors.trackingId}</p>}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Votre nom complet"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition ${
                errors.name ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <div className="flex gap-3">
              <div className="w-20 relative">
                <select
                  value={selectedCountryCode}
                  onChange={handleCountryChange}
                  className={`w-full bg-white border-2 rounded-lg px-3 py-3 text-transparent focus:outline-none transition appearance-none cursor-pointer ${
                    errors.phone ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
                  }`}
                >
                  {countryOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>{opt.label} ({opt.code})</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-900 font-medium">{selectedCountryCode}</div>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">&#9662;</div>
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneInput}
                placeholder="Numéro de téléphone"
                className={`flex-1 px-4 py-3 border-2 rounded-lg focus:outline-none transition ${
                  errors.phone ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
                }`}
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          </div>

          {/* Complaint Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de réclamation</label>
            <select
              name="complaintType"
              value={formData.complaintType}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition appearance-none bg-white ${
                errors.complaintType ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
              }`}
            >
              {complaintTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {errors.complaintType && <p className="mt-1 text-sm text-red-600">{errors.complaintType}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description du problème</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Décrivez votre problème en détail..."
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition resize-none ${
                errors.description ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-custom'
              }`}
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pièces jointes (optionnel)</label>
            <p className="text-xs text-gray-500 mb-2">Images uniquement, 5 Mo max par fichier, 5 fichiers max.</p>
            <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-custom transition">
              <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm text-gray-500">Ajouter des photos</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {errors.attachments && <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {attachments.map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit error */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {submitError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary-custom hover:bg-primary-custom/90 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-70 flex items-center justify-center"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Envoi en cours...
              </span>
            ) : 'Envoyer la réclamation'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ComplaintPage;
